import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { lighten, makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Toolbar from "@material-ui/core/Toolbar";
import EuroIcon from "@material-ui/icons/EuroSymbol";
import Paper from "@material-ui/core/Paper";

import {
  Event as ScheduleIcon,
  PictureAsPdf as PdfIcon,
  GridOn as ExcelIcon,
  Mail as MailIcon,
  RemoveCircleRounded as PersonRemoveIcon,
  AddBoxRounded as PersonAddIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  RestoreFromTrash as RestoreIcon,
  PhoneAndroid as PhoneIcon,
  Block as BlockIcon,
  RemoveCircleOutline as RemoveCircleIcon,
  Report,
  Block,
} from "@material-ui/icons";
import "./style.css";
import SelectInput from "../SelectInput";
import PageTitle from "../../components/PageTitle/PageTitle";

function descendingComparator(a, b, orderBy) {
  if (typeof b[orderBy] === "number") {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
  } else {
    const bValue = b[orderBy] ? b[orderBy].toLowerCase() : "";
    const aValue = a[orderBy] ? a[orderBy].toLowerCase() : "";

    if (bValue < aValue) {
      return -1;
    }
    if (bValue > aValue) {
      return 1;
    }
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props) {
  const {
    classes,
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const headCells = props.headCells;

  return (
    <TableHead>
      <TableRow>
        <TableCell></TableCell>

        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            //align={headCell.numeric ? 'right' : 'left'}
            align={props.noCerca ? "center" : "left"}
            padding={false ? "none" : "default"}
            sortDirection={orderBy === headCell.id ? order : false}
            style={{
              fontWeight: "bold",
              width: headCell.id === "__" ? "20px" : "auto",
            }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === "light"
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: {
    flex: "1 1 100%",
  },
}));

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles();
  const { numSelected } = props;

  return (
    <Toolbar
      className={clsx(classes.root, {
        [classes.highlight]: numSelected > 0,
      })}
    ></Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

//create your forceUpdate hook
function useForceUpdate() {
  const [value, setValue] = useState(0); // integer state
  return () => setValue((value) => value + 1); // update the state to force render
}

export default function EnhancedTable(props) {
  const classes = useStyles();
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState(props.headCells[0].id);
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [searchText, setSearchText] = React.useState("");
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  var rows = props.rows || [];

  if (searchText !== "") {
    rows = [];
    props.rows.forEach((r) => {
      var found = false;
      var lowerSearch = searchText.toLowerCase();

      props.searchCols.forEach((element) => {
        if (r[element] && r[element].toLowerCase().indexOf(lowerSearch) !== -1)
          found = true;
      });

      if (found === true) rows.push(r);
    });
  }

  const forceUpdate = useForceUpdate();

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.eventName);
      //   setSelected(newSelecteds);
      return;
    }
    //   setSelected([]);
  };

  const handleChange = (e) => {
    setSearchText(e.target.value);
    forceUpdate();
  };

  const resetSearch = () => {
    if (searchRef.current !== null) {
      searchRef.current.value = "";
      setSearchText("");
    }
    forceUpdate();
  };

  const handleClick = (id, name) => {
    if (props.onEventClick) props.onEventClick(id);

    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    //   setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  var searchRef = React.createRef();

  return (
    <div className={classes.root}>
      {!props.noCerca && (
        <div style={{ marginBottom: 10, display: "inline-flex" }}>
          <span style={{ fontFamily: "Glacial Indifference" }}> Cerca:</span>

          <input
            ref={searchRef}
            value={searchText}
            style={{ marginLeft: 10 }}
            type="text"
            onChange={(e) => {
              handleChange(e);
            }}
          ></input>
          <div
            style={{ marginLeft: 10, cursor: "pointer" }}
            onClick={resetSearch}
          >
            X
          </div>
        </div>
      )}

      {props.filterTags && (
        <div
          style={{ marginBottom: 10, display: "flex", alignItems: "center" }}
        >
          <div>
            <SelectInput
              value={props.selectedTags}
              name="Tag"
              hidable={false}
              options={props.filterTags}
              isMulti={true}
              onChange={props.handleTagChange}
            />
          </div>
        </div>
      )}

      {props.filterChiamati && (
        <div
          style={{ marginBottom: 10, display: "flex", alignItems: "center" }}
        >
          <div>
            <SelectInput
              value={props.prossimoAnno}
              name=""
              hidable={false}
              options={props.filterChiamati}
              isMulti={false}
              onChange={props.handleProssimoAnno}
            />
          </div>
        </div>
      )}

      {props.categorie && (
        <div
          style={{ marginBottom: 10, display: "flex", alignItems: "center" }}
        >
          <div>
            <SelectInput
              value={props.selectedCategoria}
              name="Categoria"
              hidable={false}
              options={props.categorie}
              isMulti={false}
              onChange={props.onChangeCategoria}
            />
          </div>

          {props.corsiAssociati && (
            <div style={{ marginLeft: 10 }}>
              <SelectInput
                value={props.selectedCorsi}
                name="Corsi"
                hidable={false}
                options={props.corsiAssociati}
                isMulti={false}
                onChange={props.onChangeCorsi}
              />
            </div>
          )}
        </div>
      )}

      <Paper className={classes.paper}>
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
              headCells={props.headCells}
            />
            <TableBody>
              {stableSort(rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isItemSelected = isSelected(row.eventName);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  const bgColor =
                    props.type !== undefined &&
                    props.type === "billing" &&
                    (row.corsi_ritiro.includes(props.currentCourse + ";") ||
                      row.disabled === "1")
                      ? "#E7A9A2"
                      : index % 2 === 0
                      ? "#ffffff"
                      : "#f5f5f5";

                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.id}
                      selected={isItemSelected}
                      style={{
                        backgroundColor: bgColor,
                      }}
                    >
                      <TableCell padding="checkbox">
                        {/* <CheckBox elementId={row.id} onElementCheck={props.onElementCheck}/> */}
                      </TableCell>

                      {props.headCells.map((elem, indexColonna) => {
                        return (
                          <TableCell
                            onClick={(event) => {
                              if (!props.onCellClick) {
                                handleClick(row.id);
                              }
                            }}
                            align={"left"}
                            style={{
                              margin: "0",
                              padding: "0",
                              paddingLeft: "20px",
                              height: "0px",
                              cursor: props.onCellClick ? "pointer" : "default",
                            }}
                            sx={{
                              height: "0px !important",
                            }}
                            key={elem.id}
                          >
                            {elem.id === "tag_name" ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                }}
                              >
                                {row.tag_name || row.tag ? (
                                  (row.tag_name ? row.tag_name : row.tag)
                                    .split(",")
                                    .map((tag, index) => (
                                      <span
                                        key={index}
                                        style={{
                                          margin: "0 5px",
                                          padding: "2px 5px",
                                          border: "1px solid #ccc",
                                          borderRadius: "3px",
                                          backgroundColor: "#f0f0f0",
                                        }}
                                      >
                                        {tag.trim()}{" "}
                                      </span>
                                    ))
                                ) : (
                                  <span>Nessun Tag</span>
                                )}
                              </div>
                            ) : (
                              <div
                                onClick={
                                  props.onCellClick
                                    ? () => {
                                        props.onCellClick(row.id, elem.id);
                                      }
                                    : null
                                }
                                style={{
                                  margin: "0",
                                  padding: "0",
                                }}
                              >
                                {row[elem.id]}
                              </div>
                            )}
                          </TableCell>
                        );
                      })}

                      {(props.onDelete ||
                        props.calendar ||
                        props.invoices ||
                        props.pdf ||
                        props.email ||
                        props.phone ||
                        props.blacklist ||
                        props.addPerson ||
                        props.removePerson) && (
                        <TableCell
                          align="right"
                          className="iconsColumn"
                          style={{ display: "flex" }}
                        >
                          {props.calendar !== undefined &&
                            props.calendar === true && (
                              <ScheduleIcon
                                className="icon"
                                style={{
                                  marginRight: 20,
                                  height: "20px",
                                  width: "20px",
                                }}
                                onClick={() => {
                                  props.onCalendarClick(row.id);
                                  //                          window.location.href= '#/app/schedule';
                                }}
                              />
                            )}
                          {props.invoices && (
                            <EuroIcon
                              className="icon"
                              style={{ height: "20px", width: "20px" }}
                              onClick={() => props.onInvoicesPressed(row.id)}
                            />
                          )}

                          {props.blackList &&
                            (props.blackList.includes(row.id) ? (
                              <RemoveCircleIcon
                                className="icon"
                                style={{
                                  height: "20px",
                                  width: "20px",
                                  color: "red",
                                }}
                                onClick={() => props.onBlacklistPressed(row.id)}
                              />
                            ) : (
                              <BlockIcon
                                className="icon"
                                style={{ height: "20px", width: "20px" }}
                                onClick={() => props.onBlacklistPressed(row.id)}
                              />
                            ))}

                          {props.pdf !== undefined && props.pdf === true && (
                            <PdfIcon
                              className="icon"
                              style={{ height: "20px", width: "20px" }}
                              onClick={() => {
                                props.onPdfClicked &&
                                  props.onPdfClicked(row.id);
                              }}
                            />
                          )}

                          {props.mail !== undefined && props.mail === true && (
                            <MailIcon
                              className="icon"
                              style={{ height: "20px", width: "20px" }}
                              onClick={() =>
                                props.onSendMail && props.onSendMail(row.id)
                              }
                            />
                          )}
                          {props.phone !== undefined &&
                            props.phone === true && (
                              <PhoneIcon
                                className="icon"
                                style={{ height: "20px", width: "20px" }}
                                onClick={() =>
                                  props.onSendNotification &&
                                  props.onSendNotification(row.id)
                                }
                              />
                            )}
                          {props.onDelete && props.corsi ? (
                            <ArchiveIcon
                              className="icon"
                              style={{ height: "20px", width: "20px" }}
                              onClick={() => props.onDelete(row.id)}
                            />
                          ) : (
                            props.onDelete && (
                              <DeleteIcon
                                className="icon"
                                style={{ height: "20px", width: "20px" }}
                                onClick={() => props.onDelete(row.id)}
                              />
                            )
                          )}
                          {props.onRestore && (
                            <RestoreIcon
                              className="icon"
                              style={{ height: "20px", width: "20px" }}
                              onClick={() => props.onRestore(row.id)}
                            />
                          )}
                          {props.assenze && (
                            <Report
                              className="icon"
                              style={{ height: "20px", width: "20px" }}
                              onClick={() => props.onAssenze(row.id)}
                            />
                          )}
                          {props.students !== undefined &&
                            props.students &&
                            props.addPerson &&
                            !row.corsi_ritiro.includes(
                              props.currentCourse + ";"
                            ) && (
                              <PersonRemoveIcon
                                className="icon"
                                style={{ height: "20px", width: "20px" }}
                                onClick={() =>
                                  props.onRemoveStudent &&
                                  props.onRemoveStudent(row.id)
                                }
                              />
                            )}
                          {props.students !== undefined &&
                            props.students &&
                            props.removePerson &&
                            row.corsi_ritiro.includes(
                              props.currentCourse + ";"
                            ) && (
                              <PersonAddIcon
                                className="icon"
                                style={{ height: "20px", width: "20px" }}
                                onClick={() =>
                                  props.onAddStudent &&
                                  props.onAddStudent(row.id)
                                }
                              />
                            )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[25]}
          component="div"
          count={rows.length}
          rowsPerPage={25}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
}
